import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import BalanceCard from './BalanceCard';
import CustomSnackbar from '../../components/ui/Snackbar';
import Rows from '../../components/ui/Rows';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import { formatAmountForDisplay } from '../../utils/amountFormatter';

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCaisse, setSelectedCaisse] = useState(null);
  const [isCaisseDropdownOpen, setIsCaisseDropdownOpen] = useState(false);
  const [caisseOptions, setCaisseOptions] = useState([]);
  const [caisseBalances, setCaisseBalances] = useState({ caisse: 0, coffre: 0 });
  const [activeDeskAgent, setActiveDeskAgent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Lists data state
  const [categoriesData, setCategoriesData] = useState([]);
  const [coffreTransactionsData, setCoffreTransactionsData] = useState([]);
  
  // Date range state for both lists
  const [categoriesDateRange, setCategoriesDateRange] = useState(null);
  const [coffreDateRange, setCoffreDateRange] = useState(null);
  
  // State for sorting
  const [categoriesSortBy, setCategoriesSortBy] = useState('categoryName');
  const [categoriesSortDirection, setCategoriesSortDirection] = useState('ASC');
  const [coffreSortBy, setCoffreSortBy] = useState('createdAt');
  const [coffreSortDirection, setCoffreSortDirection] = useState('DESC');


  // Function to get caisse-specific data from the already loaded data
  const getCaisseData = useCallback((caisseId) => {
    if (!dashboardData?.caisses) return { 
      caisse: 0, 
      coffre: 0, 
      activeDeskAgent: null 
    };
    
    const selectedCaisseData = dashboardData.caisses.find(caisse => caisse.id === caisseId);
    
    if (!selectedCaisseData) return { 
      caisse: 0, 
      coffre: 0, 
      activeDeskAgent: null 
    };
    
    return {
      caisse: selectedCaisseData.caisseBalance || 0,
      coffre: selectedCaisseData.coffreBalance || 0,
      activeDeskAgent: selectedCaisseData.activeDeskAgent
    };
  }, [dashboardData]);

  // Fetch categories data
  const fetchCategoriesData = useCallback(async (caisseId, dateRange) => {
    if (!caisseId) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      const response = await dashboardAPI.getCategoriesSummary(caisseId, fromDate, toDate, categoriesSortBy, categoriesSortDirection);
      const categories = response.data.categories || [];
      
      // Transform data for display
      const transformedData = categories.map(category => {
        const isMixed = category.caisseFactor !== 0 && category.coffreFactor !== 0;
        const isNeutral = category.caisseFactor === 0 && category.coffreFactor === 0;
        
        let amountDisplay;
        let amountColor;
        
        // Backend sends effective amounts (already multiplied by factor)
        if (isMixed || isNeutral) {
          // Mixed or neutral transactions - no sign
          amountDisplay = formatAmountForDisplay(category.sum, false).text;
          amountColor = '#027BC6'; // Blue for neutral/mixed
        } else {
          // Pure transactions - effective amount with sign
          const formatted = formatAmountForDisplay(category.sum, true);
          amountDisplay = formatted.text;
          amountColor = formatted.color;
        }
        
        return {
          categoryName: category.categoryName,
          sum: amountDisplay,
          isMixed,
          isNeutral,
          amountColor
        };
      });
      
      setCategoriesData(transformedData);
    } catch (error) {
      console.error('Error fetching categories data:', error);
      setCategoriesData([]);
    }
  }, [categoriesSortBy, categoriesSortDirection]);

  // Fetch coffre transactions data
  const fetchCoffreTransactionsData = useCallback(async (caisseId, dateRange) => {
    if (!caisseId) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      const response = await dashboardAPI.getCoffreTransactions(caisseId, fromDate, toDate, coffreSortBy, coffreSortDirection);
      const transactions = response.data || [];
      
      // Transform data for display
      const transformedData = transactions.map(transaction => {
        const factor = transaction.factor || 1;
        const adjustedAmount = transaction.amount * factor;
        const formatted = formatAmountForDisplay(adjustedAmount, true);
        
        return {
          createdAt: transaction.date,
          amount: formatted.text,
          amountColor: formatted.color,
          agentName: transaction.by
        };
      });
      
      setCoffreTransactionsData(transformedData);
    } catch (error) {
      console.error('Error fetching coffre transactions data:', error);
      setCoffreTransactionsData([]);
    }
  }, [coffreSortBy, coffreSortDirection]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Managers see all caisses; agents see only their assigned one
        if (user.role === 'AGENT') {
          const assigned = user.assignedCaisses && user.assignedCaisses[0] ? [{ value: user.assignedCaisses[0].id, label: user.assignedCaisses[0].name }] : [];
          setCaisseOptions(assigned);
          const caisseId = assigned[0]?.value;
          if (caisseId) {
            const resp = await dashboardAPI.getCaisseDashboard(caisseId);
            const data = resp.data || {};
            setDashboardData({ caisses: [{ id: caisseId, name: assigned[0].label, caisseBalance: data.caisseBalance || 0, coffreBalance: data.coffreBalance || 0, activeDeskAgent: data.activeDeskAgent || null }] });
            setSelectedCaisse(assigned[0]);
          }
        } else {
          // Manager flow
          const dashboardResponse = await dashboardAPI.getAllCaissesDashboard();
          setDashboardData(dashboardResponse.data);
          const caisses = dashboardResponse.data.caisses
            .map(caisse => ({ value: caisse.id, label: caisse.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setCaisseOptions(caisses);
          if (caisses.length > 0) setSelectedCaisse(caisses[0]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Échec du chargement des données du tableau de bord');
        setSnackbar({
          open: true,
          message: 'Erreur lors du chargement des données du tableau de bord',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Update caisse data when selectedCaisse changes
  useEffect(() => {
    if (selectedCaisse?.value && dashboardData?.caisses) {
      const caisseData = getCaisseData(selectedCaisse.value);
      setCaisseBalances({
        caisse: caisseData.caisse,
        coffre: caisseData.coffre
      });
      setActiveDeskAgent(caisseData.activeDeskAgent);
    }
  }, [selectedCaisse, dashboardData, getCaisseData]);

  // Fetch lists data when caisse changes
  useEffect(() => {
    if (selectedCaisse?.value) {
      fetchCategoriesData(selectedCaisse.value, categoriesDateRange);
      fetchCoffreTransactionsData(selectedCaisse.value, coffreDateRange);
    }
  }, [selectedCaisse, categoriesDateRange, coffreDateRange, categoriesSortBy, categoriesSortDirection, coffreSortBy, coffreSortDirection, fetchCategoriesData, fetchCoffreTransactionsData]);

  // Handle clicking outside caisse dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCaisseDropdownOpen) {
        // Check if click is outside the dropdown
        const dropdownElement = event.target.closest('[data-caisse-dropdown]');
        if (!dropdownElement) {
          setIsCaisseDropdownOpen(false);
        }
      }
    };

    if (isCaisseDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCaisseDropdownOpen]);

  // Date range change handlers
  const handleCategoriesDateRangeChange = (dateRange) => {
    setCategoriesDateRange(dateRange);
    if (selectedCaisse?.value) {
      fetchCategoriesData(selectedCaisse.value, dateRange);
    }
  };

  const handleCoffreDateRangeChange = (dateRange) => {
    setCoffreDateRange(dateRange);
    if (selectedCaisse?.value) {
      fetchCoffreTransactionsData(selectedCaisse.value, dateRange);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }


  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  return (
    <Box>
      <Box sx={{ ...contentContainerSx, paddingTop: '24px', paddingBottom: '24px' }}>
        <Box sx={{ marginBottom: '16px', width: '100%' }}>
          <h1 
            style={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '28px',
              lineHeight: '32px',
              letterSpacing: '0px',
              color: '#38485C',
              opacity: 0.88,
              textTransform: 'none',
              margin: 0
            }}
          >
            Tableau de Bord
          </h1>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            marginBottom: '24px'
          }}
        >
          <Box position="relative" data-caisse-dropdown>
            <Box 
              display="flex" 
              alignItems="center" 
              sx={{ cursor: user.role !== 'AGENT' ? 'pointer' : 'default' }}
              onClick={() => { if (user.role !== 'AGENT') setIsCaisseDropdownOpen(!isCaisseDropdownOpen); }}
            >
              <Typography 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  letterSpacing: '0px',
                  color: '#027BC6',
                  textTransform: 'none',
                  margin: 0,
                  marginRight: '8px'
                }}
              >
                {selectedCaisse?.label || 'Bouskoura'}
              </Typography>
              {user.role !== 'AGENT' && (
                <img 
                  src={chevronDownIcon} 
                  alt="Chevron Down" 
                  className={`h-3 w-3 transform transition-transform duration-200 ${
                    isCaisseDropdownOpen ? 'rotate-180' : ''
                  }`}
                  style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)' }}
                />
              )}
            </Box>
            
            {isCaisseDropdownOpen && user.role !== 'AGENT' && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                mt={1}
                bgcolor="white"
                border="1px solid #e5e7eb"
                borderRadius="8px"
                boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                zIndex={50}
                minWidth="200px"
              >
                {caisseOptions.map((option) => (
                  <Box
                    key={option.value}
                    px={2}
                    py={1.5}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedCaisse?.value === option.value ? '#f0f9ff' : 'transparent',
                      color: selectedCaisse?.value === option.value ? '#027BC6' : '#38485C',
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: selectedCaisse?.value === option.value ? 700 : 500
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCaisse?.value !== option.value) {
                        e.target.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCaisse?.value !== option.value) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={() => {
                      setSelectedCaisse(option);
                      setIsCaisseDropdownOpen(false);
                    }}
                  >
                    {option.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {user?.role === 'MANAGER' && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#000000',
                  marginRight: '8px',
                  display: 'inline'
                }}
              >
                Utilisateur de caisse actif :
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#027BC6',
                  display: 'inline'
                }}
              >
                {activeDeskAgent ? ` ${activeDeskAgent.firstName} ${activeDeskAgent.lastName}` : ' -'}
              </Typography>
            </Box>
          )}
        </Box>

        <Box 
          display="flex" 
          gap={3} 
          mb={6}
          sx={{ width: '100%', flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: 1, minWidth: '240px' }}>
            <BalanceCard
              title="Solde Caisse"
              amount={formatAmountForDisplay(caisseBalances.caisse, false).text}
              color="blue"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: '240px' }}>
            <BalanceCard
              title="Solde Coffre"
              amount={formatAmountForDisplay(caisseBalances.coffre, false).text}
              color="green"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box 
          display="flex" 
          gap={4} 
          mb={6}
          sx={{ width: '100%', flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            <Rows
              title="Sommes par catégorie"
              showDatePicker={true}
              onDateRangeChange={handleCategoriesDateRangeChange}
              dateRange={categoriesDateRange}
              columns={[
                { key: 'categoryName', label: 'Catégories', sortable: true },
                { 
                  key: 'sum', 
                  label: 'Somme',
                  sortable: true,
                  render: (amount, row) => (
                    <span style={{ 
                      color: row.amountColor,
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      lineHeight: '19px',
                      letterSpacing: '0px',
                      textTransform: 'none',
                      opacity: 1
                    }}>
                      {amount}
                    </span>
                  )
                }
              ]}
              data={categoriesData}
              maxHeight="500px"
              sortBy={categoriesSortBy}
              sortDirection={categoriesSortDirection}
              onSort={(sortBy, sortDirection) => {
                setCategoriesSortBy(sortBy);
                setCategoriesSortDirection(sortDirection);
              }}
              containerPadding={0}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            <Rows
              title="Entrées/Sorties Coffre"
              showDatePicker={true}
              onDateRangeChange={handleCoffreDateRangeChange}
              dateRange={coffreDateRange}
              columns={[
                { 
                  key: 'createdAt', 
                  label: 'Date', 
                  sortable: true,
                  render: (value) => new Date(value).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })
                },
                { 
                  key: 'amount', 
                  label: 'Montant',
                  sortable: true,
                  render: (amount, row) => (
                    <span style={{ 
                      color: row.amountColor,
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      lineHeight: '19px',
                      letterSpacing: '0px',
                      textTransform: 'none',
                      opacity: 1
                    }}>
                      {amount}
                    </span>
                  )
                },
                { key: 'agentName', label: 'Par', sortable: true }
              ]}
              data={coffreTransactionsData}
              maxHeight="500px"
              sortBy={coffreSortBy}
              sortDirection={coffreSortDirection}
              onSort={(sortBy, sortDirection) => {
                setCoffreSortBy(sortBy);
                setCoffreSortDirection(sortDirection);
              }}
              containerPadding={0}
            />
          </Box>
        </Box>
      </Box>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Dashboard;

